// Aggregate / chart view rendered above the ResultsTable.
//
// Two responsibilities:
//   1. Always show a count badge "X Associates · Y Candidates · Z total"
//      from the visible page buffer + backend total.
//   2. Optional group-by — when a dimension is picked, hit the new
//      server-side /api/inpact/aggregate endpoint so the chart reflects
//      the FULL filtered set, not just the current page. Mónica's user
//      cases ("How many in Europe?") need full-set numbers.
//
// Customization + export live in the shared <Chart> module; this file
// owns wiring (filter group → endpoint → click-to-filter).
import { useEffect, useMemo, useState } from "react";
import type { Contact, FilterGroup, FilterOperator } from "../types/inpact";
import type { AggregationRow } from "../types/chart";
import { useExplorerStore } from "../store/explorerStore";
import { setTopLevelRule } from "../lib/filterLogic";
import { useAggregation } from "../hooks/useAggregation";
import { useFields } from "../hooks/useFields";
import {
  Chart,
  ChartCustomizationPanel,
  ChartExportToolbar,
  DEFAULT_CUSTOMIZATION,
  loadCustomization,
  saveCustomization,
  type ChartCustomization,
} from "./Chart";

interface Props {
  rows: Contact[];
  total: number;
}

export function AggregateView({ total }: Props) {
  const [group, setGroup] = useState("");
  const filterGroup = useExplorerStore((s) => s.filterGroup);
  const setFilterGroup = useExplorerStore((s) => s.setFilterGroup);
  const { fields } = useFields();
  const groupOptions = useMemo(
    () => fields.filter((field) => field.capabilities.includes("aggregate")),
    [fields],
  );
  const selectedField = groupOptions.find((field) => field.key === group);
  const groupByField = selectedField?.available ? selectedField.key : "";

  // Badge counts are computed by the backend over the FULL filtered set, not
  // the visible page — the earlier page-only computation under-counted when
  // page_size < total. Drives the "X Associates · Y Candidates" badge only.
  const { data: badgeData } = useAggregation({
    filterGroup,
    groupBy: "contact.INPACT_Associate_Status__c",
    chartType: "table",
    enabled: true,
  });
  const counts = useMemo(() => {
    const out = { associates: 0, candidates: 0 };
    for (const r of badgeData?.rows ?? []) {
      if (r.group === "Associates") out.associates = r.count;
      else if (r.group === "Candidate") out.candidates = r.count;
    }
    return out;
  }, [badgeData]);
  // Customization persists per (view, group_by) so toggling between groups
  // doesn't blow away the user's preferred layout per dimension.
  const [customization, setCustomization] = useState<ChartCustomization>(DEFAULT_CUSTOMIZATION);
  useEffect(() => {
    if (!groupByField) return;
    setCustomization(loadCustomization("explorer", groupByField));
  }, [groupByField]);

  const updateCustomization = (next: ChartCustomization) => {
    setCustomization(next);
    if (groupByField) saveCustomization("explorer", groupByField, next);
  };

  const { data, isLoading, isFetching, error } = useAggregation({
    filterGroup,
    groupBy: groupByField,
    chartType: customization.chartType,
    // For alphabetical sort, don't let the SERVER apply top_n: it caps by count
    // before the alpha order exists, hiding alphabetically-early small groups
    // inside a count-based "Other". Fetch all groups and let the client sort
    // alpha then cap (prepareRows -> applyTopN) so the cap respects alpha order.
    topN: customization.sortMode === "alpha" ? undefined : customization.topN,
    sort: customization.sortMode === "alpha" ? "desc" : customization.sortMode,
    enabled: Boolean(groupByField),
  });

  const onSliceClick = (row: AggregationRow) => {
    const canFilter = selectedField?.capabilities.includes("filter");
    // Ignore the synthetic rollup bucket and the null/empty bucket — keyed off
    // the backend flags, not literal labels, so a real category named "Other"
    // stays clickable. (Filtering for the "(unspecified)" string would match
    // zero rows instead of the null/empty records.)
    if (!selectedField || !canFilter || !row.group || row.rollup || row.is_empty) return;
    const setValued = selectedField.kind === "multipicklist"
      || selectedField.kind === "picklist"
      || selectedField.kind === "country";
    const op: FilterOperator = selectedField.kind === "multipicklist"
      ? "INCLUDES"
      : setValued ? "IN" : "=";
    const scalar = selectedField.kind === "number" ? Number(row.group) : row.group;
    const rule = {
      field: selectedField.key,
      op,
      value: setValued ? [row.group] : scalar,
    };
    const next: FilterGroup = setTopLevelRule(filterGroup, selectedField.key, rule);
    setFilterGroup(next);
  };

  const exportId = `explorer-chart-${group || "none"}`;
  const responseField = groupOptions.find(
    (field) => field.key === data?.chart_spec.group_by,
  );
  const title = `INPACT — Breakdown by ${responseField?.label ?? ""}`;

  return (
    <div className="aggregate-view" aria-label="Aggregate counts">
      <div className="aggregate-view__header">
        <div className="count-badge" aria-live="polite">
          <span>{counts.associates} Associates</span>
          <span aria-hidden="true">·</span>
          <span>{counts.candidates} Candidates</span>
          <span aria-hidden="true">·</span>
          <span className="count-badge__total">{total} total</span>
        </div>
        <label className="group-by-select">
          <span>Group by:</span>
          <select
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            aria-label="Group results by"
          >
            <option value="">None</option>
            {groupOptions.map((field) => (
              <option key={field.key} value={field.key} disabled={!field.available}>
                {field.label}{!field.available
                  ? ` (${field.unavailable_reason ?? "not available in Salesforce"})`
                  : ""}
              </option>
            ))}
          </select>
        </label>
      </div>

      {groupByField !== "" && (
        <div className="aggregate-view__body aggregate-view__body--stacked">
          {error && (
            <div className="aggregate-view__error" role="alert">
              Couldn't load breakdown: {error.message}
            </div>
          )}
          {isLoading && !data && (
            <div className="aggregate-view__loading" role="status">Loading breakdown…</div>
          )}
          {isFetching && !isLoading && (
            <div className="aggregate-view__loading" role="status">Refreshing breakdown…</div>
          )}
          {data && (
            <>
              {data.provenance.is_stale && (
                <p className="aggregate-view__suppression" role="status">
                  Showing the last authorized snapshot; refresh is currently unavailable.
                </p>
              )}
              {data.rows.some((row) => row.suppressed) && (
                <p className="aggregate-view__suppression" role="note">
                  Small groups are combined to protect privacy. Their labels are not shown.
                </p>
              )}
              <ChartExportToolbar
                result={data}
                exportId={exportId}
                title={title}
                chartType={customization.chartType}
                disabled={isFetching}
              />
              <Chart
                result={data}
                onSliceClick={isFetching ? undefined : onSliceClick}
                customization={customization}
                exportId={exportId}
              />
              <ChartCustomizationPanel
                value={customization}
                onChange={updateCustomization}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
